import React, { useState, useEffect } from 'react';

interface SmtpConfig {
    host: string;
    port: number;
    secure: boolean;
    user: string;
    pass: string;
    fromName: string;
    fromEmail: string;
}

interface ReminderRule {
    _id?: string;
    daysBefore: number;
    subject: { en: string; sv: string };
    bodyIntro: { en: string; sv: string };
    enabled: boolean;
}

interface EmailSettingsData {
    smtp: SmtpConfig;
    reminderRules: ReminderRule[];
    confirmationEmail: {
        subject: { en: string; sv: string };
        enabled: boolean;
    };
    branding: {
        logoUrl: string;
        primaryColor: string;
        footerText: { en: string; sv: string };
    };
    cronEnabled: boolean;
}

const defaultSettings: EmailSettingsData = {
    smtp: { host: 'smtp.office365.com', port: 587, secure: false, user: '', pass: '', fromName: 'Lumi Tours', fromEmail: 'sales@lumitours.com' },
    reminderRules: [],
    confirmationEmail: { subject: { en: 'Booking Confirmation — Lumi Tours', sv: 'Bokningsbekräftelse — Lumi Tours' }, enabled: true },
    branding: { logoUrl: '', primaryColor: '#0F766E', footerText: { en: 'Lumi Tours — Premium Travel Experiences', sv: 'Lumi Tours — Premium Reseupplevelser' } },
    cronEnabled: true
};

const EmailSettingsManager: React.FC = () => {
    const [settings, setSettings] = useState<EmailSettingsData>(defaultSettings);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [testEmail, setTestEmail] = useState('');
    const [testSending, setTestSending] = useState(false);
    const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
    const [activeSection, setActiveSection] = useState<'smtp' | 'reminders' | 'branding'>('smtp');

    useEffect(() => {
        fetchSettings();
    }, []);

    const fetchSettings = async () => {
        try {
            const res = await fetch('/api/email/settings');
            const data = await res.json();
            if (data.success && data.settings) {
                setSettings({ ...defaultSettings, ...data.settings });
            }
        } catch (err) {
            console.error('Failed to fetch email settings:', err);
        } finally {
            setLoading(false);
        }
    };

    const saveSettings = async () => {
        setSaving(true);
        setMessage(null);
        try {
            const res = await fetch('/api/email/settings', {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(settings)
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: 'Settings saved successfully!' });
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to save settings' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Network error' });
        } finally {
            setSaving(false);
        }
    };

    const sendTestEmail = async () => {
        if (!testEmail) return;
        setTestSending(true);
        setMessage(null);
        try {
            const res = await fetch('/api/email/test', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ to: testEmail })
            });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: `Test email sent to ${testEmail}!` });
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to send test email' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Network error — check SMTP settings' });
        } finally {
            setTestSending(false);
        }
    };

    const processReminders = async () => {
        setMessage(null);
        try {
            const res = await fetch('/api/email/process-reminders', { method: 'POST' });
            const data = await res.json();
            if (data.success) {
                setMessage({ type: 'success', text: `Reminders processed: ${data.sent}/${data.processed} sent` });
            } else {
                setMessage({ type: 'error', text: data.message || 'Failed to process reminders' });
            }
        } catch (err) {
            setMessage({ type: 'error', text: 'Network error' });
        }
    };

    const addReminderRule = () => {
        setSettings(prev => ({
            ...prev,
            reminderRules: [...prev.reminderRules, {
                daysBefore: 3,
                subject: { en: 'Your trip is coming up!', sv: 'Din resa närmar sig!' },
                bodyIntro: { en: 'Get ready for your upcoming trip!', sv: 'Gör dig redo för din kommande resa!' },
                enabled: true
            }]
        }));
    };

    const removeReminderRule = (index: number) => {
        setSettings(prev => ({
            ...prev,
            reminderRules: prev.reminderRules.filter((_, i) => i !== index)
        }));
    };

    const updateReminderRule = (index: number, field: string, value: any) => {
        setSettings(prev => {
            const rules = [...prev.reminderRules];
            if (field.includes('.')) {
                const [parent, child] = field.split('.');
                rules[index] = { ...rules[index], [parent]: { ...rules[index][parent], [child]: value } };
            } else {
                rules[index] = { ...rules[index], [field]: value };
            }
            return { ...prev, reminderRules: rules };
        });
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-spin w-8 h-8 border-2 border-teal-500 border-t-transparent rounded-full"></div>
            </div>
        );
    }

    const inputClass = "w-full px-4 py-3 bg-slate-700 border border-slate-600 rounded-xl text-white placeholder:text-slate-400 focus:ring-2 focus:ring-teal-500 focus:border-transparent outline-none transition-all text-sm";
    const labelClass = "block text-xs font-bold text-slate-400 uppercase tracking-wider mb-1.5";

    return (
        <div className="flex-1 overflow-y-auto p-4 md:p-8 bg-slate-900 min-h-screen">
            <div className="max-w-5xl mx-auto">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
                    <div>
                        <h1 className="text-3xl font-black text-white flex items-center gap-3">
                            <span className="w-12 h-12 bg-gradient-to-br from-teal-500 to-emerald-600 rounded-xl flex items-center justify-center text-2xl shadow-lg shadow-teal-500/20">📧</span>
                            Email Settings
                        </h1>
                        <p className="text-slate-400 mt-1">Configure SMTP, booking emails and automated reminders</p>
                    </div>
                    <div className="flex gap-3">
                        <button
                            onClick={processReminders}
                            className="px-5 py-2.5 bg-slate-700 hover:bg-slate-600 text-white rounded-xl font-medium text-sm transition-all border border-slate-600 flex items-center gap-2"
                        >
                            <span>⏰</span> Run Reminders Now
                        </button>
                        <button
                            onClick={saveSettings}
                            disabled={saving}
                            className="px-6 py-2.5 bg-gradient-to-r from-teal-500 to-emerald-600 hover:from-teal-600 hover:to-emerald-700 text-white rounded-xl font-bold text-sm shadow-lg shadow-teal-500/25 transition-all disabled:opacity-50 flex items-center gap-2"
                        >
                            {saving ? <span className="animate-spin">⏳</span> : <span>💾</span>}
                            {saving ? 'Saving...' : 'Save Settings'}
                        </button>
                    </div>
                </div>

                {/* Message */}
                {message && (
                    <div className={`mb-6 p-4 rounded-xl border text-sm font-medium ${
                        message.type === 'success' ? 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400' : 'bg-red-500/10 border-red-500/30 text-red-400'
                    }`}>
                        {message.type === 'success' ? '✅' : '❌'} {message.text}
                    </div>
                )}

                {/* Tab Navigation */}
                <div className="flex gap-1 mb-6 bg-slate-800 p-1 rounded-xl border border-slate-700">
                    {[
                        { id: 'smtp' as const, label: 'SMTP Configuration', icon: '🔧' },
                        { id: 'reminders' as const, label: 'Reminder Rules', icon: '⏰' },
                        { id: 'branding' as const, label: 'Template & Branding', icon: '🎨' }
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveSection(tab.id)}
                            className={`flex-1 py-3 px-4 rounded-lg text-sm font-bold transition-all flex items-center justify-center gap-2 ${
                                activeSection === tab.id
                                    ? 'bg-gradient-to-r from-teal-500 to-emerald-600 text-white shadow-lg'
                                    : 'text-slate-400 hover:text-white hover:bg-slate-700'
                            }`}
                        >
                            <span>{tab.icon}</span> {tab.label}
                        </button>
                    ))}
                </div>

                {/* SMTP Configuration */}
                {activeSection === 'smtp' && (
                    <div className="space-y-6">
                        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                <span className="w-8 h-8 bg-blue-500/20 rounded-lg flex items-center justify-center text-blue-400">🔧</span>
                                SMTP Server Settings
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>SMTP Host</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.smtp.host}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, host: e.target.value } }))}
                                        placeholder="smtp.office365.com"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Port</label>
                                    <input
                                        type="number"
                                        className={inputClass}
                                        value={settings.smtp.port}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, port: parseInt(e.target.value) || 587 } }))}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Username</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.smtp.user}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, user: e.target.value } }))}
                                        placeholder="sales@lumitours.com"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Password</label>
                                    <input
                                        type="password"
                                        className={inputClass}
                                        value={settings.smtp.pass}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, pass: e.target.value } }))}
                                        placeholder="••••••••"
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>From Name</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.smtp.fromName}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, fromName: e.target.value } }))}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>From Email</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.smtp.fromEmail}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, fromEmail: e.target.value } }))}
                                    />
                                </div>
                            </div>
                            <div className="mt-4 flex items-center gap-3">
                                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.smtp.secure}
                                        onChange={e => setSettings(s => ({ ...s, smtp: { ...s.smtp, secure: e.target.checked } }))}
                                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500"
                                    />
                                    Use SSL/TLS (check for port 465, leave unchecked for STARTTLS port 587)
                                </label>
                            </div>
                        </div>

                        {/* Test Email */}
                        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                            <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                                <span className="w-8 h-8 bg-amber-500/20 rounded-lg flex items-center justify-center text-amber-400">📨</span>
                                Test Connection
                            </h3>
                            <div className="flex gap-3">
                                <input
                                    type="email"
                                    className={inputClass + ' flex-1'}
                                    value={testEmail}
                                    onChange={e => setTestEmail(e.target.value)}
                                    placeholder="Enter test email address..."
                                />
                                <button
                                    onClick={sendTestEmail}
                                    disabled={testSending || !testEmail}
                                    className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white rounded-xl font-bold text-sm transition-all disabled:opacity-50 whitespace-nowrap flex items-center gap-2"
                                >
                                    {testSending ? <span className="animate-spin">⏳</span> : <span>🚀</span>}
                                    {testSending ? 'Sending...' : 'Send Test'}
                                </button>
                            </div>
                        </div>
                    </div>
                )}

                {/* Reminder Rules */}
                {activeSection === 'reminders' && (
                    <div className="space-y-6">
                        {/* Cron toggle */}
                        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <h3 className="text-lg font-bold text-white flex items-center gap-2">
                                        <span className="w-8 h-8 bg-purple-500/20 rounded-lg flex items-center justify-center text-purple-400">🤖</span>
                                        Automated Reminders
                                    </h3>
                                    <p className="text-slate-400 text-sm mt-1">Automatically send reminders before check-in date (runs daily at 09:00)</p>
                                </div>
                                <label className="relative inline-flex items-center cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.cronEnabled}
                                        onChange={e => setSettings(s => ({ ...s, cronEnabled: e.target.checked }))}
                                        className="sr-only peer"
                                    />
                                    <div className="w-14 h-7 bg-slate-600 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all peer-checked:bg-teal-500"></div>
                                </label>
                            </div>
                        </div>

                        {/* Rules List */}
                        {settings.reminderRules.map((rule, index) => (
                            <div key={index} className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                                <div className="flex items-center justify-between mb-4">
                                    <div className="flex items-center gap-3">
                                        <span className="w-10 h-10 bg-gradient-to-br from-orange-500 to-rose-500 rounded-xl flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-orange-500/20">
                                            {rule.daysBefore}
                                        </span>
                                        <div>
                                            <span className="text-white font-bold">
                                                {rule.daysBefore} {rule.daysBefore === 1 ? 'day' : 'days'} before check-in
                                            </span>
                                            <div className="text-xs text-slate-400">Reminder #{index + 1}</div>
                                        </div>
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <label className="relative inline-flex items-center cursor-pointer">
                                            <input
                                                type="checkbox"
                                                checked={rule.enabled}
                                                onChange={e => updateReminderRule(index, 'enabled', e.target.checked)}
                                                className="sr-only peer"
                                            />
                                            <div className="w-11 h-6 bg-slate-600 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-teal-500"></div>
                                        </label>
                                        <button
                                            onClick={() => removeReminderRule(index)}
                                            className="p-2 text-slate-400 hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-all"
                                        >
                                            🗑️
                                        </button>
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className={labelClass}>Days Before Check-in</label>
                                        <input
                                            type="number"
                                            className={inputClass}
                                            value={rule.daysBefore}
                                            onChange={e => updateReminderRule(index, 'daysBefore', parseInt(e.target.value) || 1)}
                                            min={1}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Subject (EN)</label>
                                        <input
                                            type="text"
                                            className={inputClass}
                                            value={rule.subject.en}
                                            onChange={e => updateReminderRule(index, 'subject.en', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Subject (SV)</label>
                                        <input
                                            type="text"
                                            className={inputClass}
                                            value={rule.subject.sv}
                                            onChange={e => updateReminderRule(index, 'subject.sv', e.target.value)}
                                        />
                                    </div>
                                    <div>
                                        <label className={labelClass}>Intro Text (EN)</label>
                                        <input
                                            type="text"
                                            className={inputClass}
                                            value={rule.bodyIntro.en}
                                            onChange={e => updateReminderRule(index, 'bodyIntro.en', e.target.value)}
                                        />
                                    </div>
                                </div>
                            </div>
                        ))}

                        <button
                            onClick={addReminderRule}
                            className="w-full py-4 border-2 border-dashed border-slate-600 rounded-2xl text-slate-400 hover:text-teal-400 hover:border-teal-500/50 transition-all font-medium flex items-center justify-center gap-2"
                        >
                            <span className="text-xl">+</span> Add Reminder Rule
                        </button>
                    </div>
                )}

                {/* Branding */}
                {activeSection === 'branding' && (
                    <div className="space-y-6">
                        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                <span className="w-8 h-8 bg-pink-500/20 rounded-lg flex items-center justify-center text-pink-400">🎨</span>
                                Email Branding
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Primary Color</label>
                                    <div className="flex gap-2">
                                        <input
                                            type="color"
                                            className="w-12 h-12 rounded-lg border border-slate-600 cursor-pointer bg-transparent"
                                            value={settings.branding.primaryColor}
                                            onChange={e => setSettings(s => ({ ...s, branding: { ...s.branding, primaryColor: e.target.value } }))}
                                        />
                                        <input
                                            type="text"
                                            className={inputClass + ' flex-1'}
                                            value={settings.branding.primaryColor}
                                            onChange={e => setSettings(s => ({ ...s, branding: { ...s.branding, primaryColor: e.target.value } }))}
                                        />
                                    </div>
                                </div>
                                <div>
                                    <label className={labelClass}>Logo URL</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.branding.logoUrl}
                                        onChange={e => setSettings(s => ({ ...s, branding: { ...s.branding, logoUrl: e.target.value } }))}
                                        placeholder="https://..."
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Footer Text (EN)</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.branding.footerText.en}
                                        onChange={e => setSettings(s => ({ ...s, branding: { ...s.branding, footerText: { ...s.branding.footerText, en: e.target.value } } }))}
                                    />
                                </div>
                                <div>
                                    <label className={labelClass}>Footer Text (SV)</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.branding.footerText.sv}
                                        onChange={e => setSettings(s => ({ ...s, branding: { ...s.branding, footerText: { ...s.branding.footerText, sv: e.target.value } } }))}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Confirmation Email Settings */}
                        <div className="bg-slate-800 rounded-2xl border border-slate-700 p-6">
                            <h3 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
                                <span className="w-8 h-8 bg-green-500/20 rounded-lg flex items-center justify-center text-green-400">📋</span>
                                Confirmation Email
                            </h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className={labelClass}>Subject (EN)</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.confirmationEmail.subject.en}
                                        onChange={e => setSettings(s => ({ ...s, confirmationEmail: { ...s.confirmationEmail, subject: { ...s.confirmationEmail.subject, en: e.target.value } } }))}
                                    />
                                    <p className="text-xs text-slate-500 mt-1">Use <code className="bg-slate-700 px-1 rounded">{'#{reservationNumber}'}</code> for dynamic reservation number</p>
                                </div>
                                <div>
                                    <label className={labelClass}>Subject (SV)</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        value={settings.confirmationEmail.subject.sv}
                                        onChange={e => setSettings(s => ({ ...s, confirmationEmail: { ...s.confirmationEmail, subject: { ...s.confirmationEmail.subject, sv: e.target.value } } }))}
                                    />
                                </div>
                            </div>
                            <div className="mt-4">
                                <label className="flex items-center gap-2 text-sm text-slate-300 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={settings.confirmationEmail.enabled}
                                        onChange={e => setSettings(s => ({ ...s, confirmationEmail: { ...s.confirmationEmail, enabled: e.target.checked } }))}
                                        className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-teal-500 focus:ring-teal-500"
                                    />
                                    Enable automatic confirmation emails on booking
                                </label>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default EmailSettingsManager;
